import type { EntityKind } from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import type { FilterDateRange, FilterField, FilterNumberRange, FilterState } from '@parcel/filters';
import {
  Command,
  CommandBareInput,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  toast,
} from '@parcel/ui';
import { ArrowRight, Search, Sparkles } from 'lucide-react';
import * as React from 'react';
import { AskChip } from './ask-chip.js';
import { FilterChips } from './filter-chips.js';
import {
  type DynamicOptions,
  type RecordMatch,
  deriveFilterMenu,
  fieldOptions,
  filterableFields,
  pushRecentField,
  readRecentFields,
} from './filter-menu.js';
import { FilterValueSelector } from './filter-value-selector.js';

/**
 * What the bar needs from a filter schema, which is strictly less than
 * `EntityFilterSchema`: `entity` is a free string used only to label analytics, so a
 * surface with no row in the shared filter registry (connectors, say) can drive this bar
 * from a locally declared schema. Every `EntityFilterSchema` satisfies it structurally.
 */
export interface FilterBarSchema {
  entity: string;
  fields: readonly FilterField[];
}

/** An Ask translation into flat filter params, for a surface that still keys filters by field. */
export interface AskResponse {
  filters: Record<string, string | string[]>;
  q: string | null;
  unmapped: string[];
}

export interface FilterBarProps {
  schema: FilterBarSchema;
  state: FilterState;
  onStateChange: (next: FilterState) => void;
  /** Resolved options for dynamic-enum / entity fields, keyed by field key. */
  dynamicOptions?: DynamicOptions;
  /** Pre-matched record-jump rows for the current input (parent computes). */
  records?: readonly RecordMatch[];
  onSelectRecord?: (id: string) => void;
  /** Fires as the user types, with the active field for field-specific option searches. */
  onInputChange?: (text: string, activeFieldKey: string | null) => void;
  /** Table id for the per-table recently-used-fields store. */
  recentKey: string;
  placeholder?: string;
  /**
   * Enables the Ask rows. The bar calls this with the typed question and owns
   * the transient UI (in-flight chip, unmapped/failure notice); the page owns
   * the mutation + merge into FilterState. On rejection the bar falls back to
   * a plain text-search chip.
   */
  onAsk?: (query: string) => Promise<AskResponse>;
  /** Example question for the empty-menu Ask hint row. */
  askExample?: string;
}

/**
 * Ramp-style search-or-filter bar: a pill input whose dropdown mixes record
 * jumps, filter fields, and enum values, with committed filters rendered as
 * chips inline to the right. Typing only drives the menu — the surface behind it
 * changes only via onStateChange (value toggle, date apply, "Search for …" row).
 *
 * Not table-specific: signals and connectors drive it too. Anything that can name a
 * FilterField list and hold a FilterState can use it.
 */
export function FilterBar({
  schema,
  state,
  onStateChange,
  dynamicOptions,
  records = [],
  onSelectRecord,
  onInputChange,
  recentKey,
  placeholder = 'Search or filter…',
  onAsk,
  askExample,
}: FilterBarProps) {
  const [input, setInput] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [activeField, setActiveField] = React.useState<string | null>(null);
  const [editingChip, setEditingChip] = React.useState<string | null>(null);
  const [pendingAsk, setPendingAsk] = React.useState<string | null>(null);
  const commandRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Mirror the state prop so async ask handlers never merge into a stale state.
  const stateRef = React.useRef(state);
  stateRef.current = state;

  const fields = filterableFields(schema);
  // Suggested list intentionally only refreshes on open so there's no
  // row-jumping mid-interaction as the user types.
  const recentKeys = React.useMemo(
    () => (open ? readRecentFields(window.localStorage, recentKey) : []),
    [open, recentKey],
  );
  const menu = React.useMemo(
    () => deriveFilterMenu(input, schema, { dynamicOptions, recentKeys }),
    [input, schema, dynamicOptions, recentKeys],
  );
  const active = activeField ? fields.find((f) => f.key === activeField) : undefined;

  const setInputText = (text: string, fieldKey = activeField) => {
    setInput(text);
    onInputChange?.(text, fieldKey);
  };

  const closeMenu = React.useCallback(() => {
    setOpen(false);
    setActiveField(null);
  }, []);

  // Click-outside closes the bar dropdown. Scoped to the Command element
  // (pill + dropdown) — the outer flex container spans the whole toolbar row,
  // so checking against it would keep the menu open for clicks in the empty
  // space beside the bar. Chip editor popovers are portaled elsewhere and
  // manage their own outside-dismiss.
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!commandRef.current?.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, closeMenu]);

  const isSingleSelect = (field: FilterField) =>
    field.type === 'entity' || !('multi' in field) || !field.multi;

  const commitFieldValue = (field: FilterField, value: string) => {
    const current = state.fields[field.key];
    const list = Array.isArray(current) ? current : [];
    const single = isSingleSelect(field);
    let next: string[];
    if (single) {
      next = list.includes(value) ? [] : [value];
    } else {
      next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    }
    const nextFields = { ...state.fields };
    if (next.length === 0) delete nextFields[field.key];
    else nextFields[field.key] = next;
    onStateChange({ ...state, fields: nextFields });
    pushRecentField(window.localStorage, recentKey, field.key);
    if (single) {
      setActiveField(null);
      setEditingChip(null);
    }
  };

  const commitRange = (field: FilterField, range: FilterDateRange | FilterNumberRange) => {
    // Union-safe emptiness check — neither shape exposes the other's keys.
    const sides = range as Partial<Record<'after' | 'before' | 'min' | 'max', string>>;
    const isEmpty = !sides.after && !sides.before && !sides.min && !sides.max;
    const nextFields = { ...state.fields };
    if (isEmpty) delete nextFields[field.key];
    else nextFields[field.key] = range;
    onStateChange({ ...state, fields: nextFields });
    pushRecentField(window.localStorage, recentKey, field.key);
    setActiveField(null);
    setEditingChip(null);
  };

  const removeField = (key: string) => {
    const nextFields = { ...state.fields };
    delete nextFields[key];
    onStateChange({ ...state, fields: nextFields });
    setActiveField(null);
    setEditingChip(null);
  };

  const commitSearch = () => {
    const text = input.trim();
    if (!text) return;
    onStateChange({ ...state, q: text });
    setInputText('');
    closeMenu();
  };

  const runAsk = async (query: string) => {
    // One ask at a time — selecting the Ask row while one is in flight is a no-op.
    if (!onAsk || pendingAsk !== null || !query.trim()) return;
    setInputText('');
    closeMenu();
    setPendingAsk(query);
    // schema.entity, not a bare `entity`: this component takes `schema` and has no
    // `entity` binding of its own.
    // ask_submitted is typed to the five registry entities. Only they pass onAsk, so this
    // line is unreachable from a locally declared schema; the cast never widens the event.
    const askStartedAt = {
      entity: schema.entity as EntityKind,
      query,
      query_length: query.length,
    };
    try {
      const result = await onAsk(query);
      const { unmapped } = result;
      capture('ask_submitted', {
        ...askStartedAt,
        // Same shape as filter_applied's filter_count, so the two are
        // comparable: a q counts as one filter.
        produced_filters: Object.keys(result.filters).length + (result.q ? 1 : 0),
      });
      if (unmapped.length > 0) {
        // Unmapped fragments are NOT searched — say so instead of guessing.
        toast.warning(`Couldn’t translate ${unmapped.map((u) => `“${u}”`).join(', ')}`, {
          description: 'Try filterable fields like stage, type, city, or dates.',
        });
      }
    } catch {
      // Translation/network/auth failure → degrade to a plain text-search chip.
      onStateChange({ ...stateRef.current, q: query });
      toast.error('Couldn’t translate — applied your question as a text search');
    } finally {
      setPendingAsk(null);
    }
  };

  const openFieldView = (key: string) => {
    setActiveField(key);
    setInputText('', key);
  };

  const handleBackspaceOnEmpty = () => {
    if (activeField) {
      setActiveField(null);
      return;
    }
    const fieldKeys = Object.keys(state.fields);
    const lastField = fieldKeys[fieldKeys.length - 1];
    if (lastField) {
      setEditingChip(lastField);
      closeMenu();
      return;
    }
    if (state.q.trim()) {
      setInputText(state.q);
      onStateChange({ ...state, q: '' });
    }
  };

  const renderChipEditor = (field: FilterField) => (
    <FilterValueSelector
      field={field}
      value={state.fields[field.key]}
      options={fieldOptions(field, dynamicOptions)}
      onToggleValue={(v) => commitFieldValue(field, v)}
      onApplyRange={(range) => commitRange(field, range)}
      onRemove={() => removeField(field.key)}
    />
  );

  const hasTypingMatches =
    records.length > 0 || menu.fieldMatches.length > 0 || menu.valueMatches.length > 0;

  return (
    <div className="relative flex min-w-0 flex-1 flex-wrap items-center gap-2">
      <Command
        ref={commandRef}
        shouldFilter={false}
        className="w-auto max-w-[280px] flex-1 overflow-visible bg-transparent"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            if (activeField) setActiveField(null);
            else closeMenu();
            event.preventDefault();
            return;
          }
          if (
            event.key === 'Backspace' &&
            event.target === inputRef.current &&
            input.length === 0
          ) {
            handleBackspaceOnEmpty();
          }
        }}
      >
        <div className="flex h-9 min-w-[220px] items-center gap-2 rounded-full border bg-background px-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <CommandBareInput
            ref={inputRef}
            value={input}
            onValueChange={setInputText}
            placeholder={placeholder}
            onFocus={() => setOpen(true)}
          />
        </div>

        {open ? (
          <div className="absolute left-0 top-10 z-50 w-[480px] max-w-[80vw] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
            {active ? (
              renderChipEditor(active)
            ) : (
              <>
                {onAsk && askExample && menu.searchText === null ? (
                  <div className="flex items-center gap-1.5 border-b bg-brand-muted px-3 py-2.5 text-sm">
                    <span className="font-medium text-brand">Ask:</span>
                    <span className="truncate text-muted-foreground">{askExample}</span>
                    <Sparkles className="ml-auto h-4 w-4 shrink-0 text-brand" aria-hidden />
                  </div>
                ) : null}
                <CommandList className="max-h-[min(360px,50vh)]">
                  {menu.searchText === null ? (
                    <>
                      <CommandGroup heading="Suggested">
                        {menu.suggestedFields.map((field) => (
                          <FieldRow key={field.key} field={field} onSelect={openFieldView} />
                        ))}
                      </CommandGroup>
                      {menu.allFields.length > 0 ? (
                        <CommandGroup heading="All filters">
                          {menu.allFields.map((field) => (
                            <FieldRow key={field.key} field={field} onSelect={openFieldView} />
                          ))}
                        </CommandGroup>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {records.length > 0 ? (
                        <CommandGroup heading="Records">
                          {records.map((record) => (
                            <CommandItem
                              key={record.id}
                              value={`record:${record.id}`}
                              onSelect={() => {
                                onSelectRecord?.(record.id);
                                setInputText('');
                                closeMenu();
                              }}
                            >
                              <span className="truncate">{record.label}</span>
                              {record.sublabel ? (
                                <span className="truncate text-xs text-muted-foreground">
                                  · {record.sublabel}
                                </span>
                              ) : null}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ) : null}
                      {menu.fieldMatches.length > 0 ? (
                        <CommandGroup heading="Filters">
                          {menu.fieldMatches.map((field) => (
                            <FieldRow key={field.key} field={field} onSelect={openFieldView} />
                          ))}
                        </CommandGroup>
                      ) : null}
                      {menu.valueMatches.length > 0 ? (
                        <CommandGroup heading="Values">
                          {menu.valueMatches.map(({ field, option }) => (
                            <CommandItem
                              key={`${field.key}:${option.value}`}
                              value={`value:${field.key}:${option.value}`}
                              onSelect={() => {
                                commitFieldValue(field, option.value);
                                setInputText('');
                              }}
                            >
                              <span className="text-muted-foreground">{field.label}</span>
                              <span className="truncate">{option.label}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ) : null}
                      {hasTypingMatches ? <CommandSeparator alwaysRender /> : null}
                      {/* DOM order = cmdk's Enter default: with no matches the
                          Ask row is first (a full sentence defaults to Ask);
                          with matches the first match stays the default. */}
                      <CommandGroup>
                        {onAsk ? (
                          <CommandItem
                            value="ask-row"
                            onSelect={() => runAsk(menu.searchText ?? '')}
                            className="aria-selected:!bg-brand-muted"
                          >
                            <span className="font-medium text-brand">Ask:</span>
                            <span className="truncate">“{menu.searchText}”</span>
                            <Sparkles className="ml-auto h-4 w-4 shrink-0 text-brand" aria-hidden />
                          </CommandItem>
                        ) : null}
                        <CommandItem value="search-row" onSelect={commitSearch}>
                          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
                          Search for “{menu.searchText}”
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
                <div className="flex items-center justify-between border-t px-3 py-1.5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Kbd>↑</Kbd>
                    <Kbd>↓</Kbd> navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <Kbd>⏎</Kbd> select
                  </span>
                </div>
              </>
            )}
          </div>
        ) : null}
      </Command>

      {pendingAsk !== null ? <AskChip question={pendingAsk} /> : null}

      <FilterChips
        schema={schema}
        state={state}
        dynamicOptions={dynamicOptions}
        editingField={editingChip}
        onEditField={(key) => {
          setEditingChip(key);
          if (key !== null) closeMenu();
        }}
        onRemoveField={removeField}
        onRemoveSearch={() => onStateChange({ ...state, q: '' })}
        onEditSearch={() => {
          setInputText(state.q);
          onStateChange({ ...state, q: '' });
          setOpen(true);
          inputRef.current?.focus();
        }}
        renderEditor={renderChipEditor}
      />
    </div>
  );
}

function FieldRow({ field, onSelect }: { field: FilterField; onSelect: (key: string) => void }) {
  return (
    <CommandItem
      value={`field:${field.key}`}
      onSelect={() => onSelect(field.key)}
      className="px-3 py-2"
    >
      <span className="truncate">{field.label}</span>
      <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground" aria-hidden />
    </CommandItem>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border bg-muted px-1 font-sans text-[10px] text-muted-foreground">
      {children}
    </kbd>
  );
}
